import { MaterialToMasonry } from "common/materials/library/materialToMasonry";
import { pathStringFromMaterialSrc } from "common/materials/pathStringFromMaterialSrc";
import type { DocMigration } from "../docMigration";

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_92: DocMigration = {
  maxSchemaVersion: "0.0.92",
  description:
    "Auto-assign Rowlock or Header trim kind in references to masonry palette slots. Migrate palettes to use masonry.",
  apply(json: any, warnings: string[]) {
    // We used to have a convention that masonryTrim0 slot had a Rowlock material and masonryTrim1 slot had a Header material.
    // We migrate material references according to that convention.
    // If we find a palette using a different convention in the document we will not migrate that palette to masonry and warn about it.
    const trimKind0 = "Rowlock";
    const trimKind1 = "Header";

    let masonryConsistentAcrossSlots = true;

    const processPalette = (palette: any, applyChanges: boolean) => {
      if (palette === undefined) {
        return;
      }
      if (palette.type === "EXTERIOR_PALETTE") {
        const key0 = "masonryTrim0";
        const key1 = "masonryTrim1";
        let masonryFromSlot0;
        if (palette[key0] !== undefined && palette[key0] !== null && palette[key0].templateId !== undefined) {
          const materialPath = pathStringFromMaterialSrc(palette[key0]);
          masonryFromSlot0 = MaterialToMasonry[materialPath];
          if (masonryFromSlot0 !== undefined) {
            // Check if the palette in the doc follows the convention.
            if (masonryFromSlot0.trimKind !== trimKind0) {
              warnings.push(
                "masonryTrim0 slot in a palette did not contain a " +
                  trimKind0 +
                  " material. Masonry material references may not migrate consistently.",
              );
            } else if (applyChanges) {
              palette[key0] = masonryFromSlot0.masonryId;
            }
          }
        }
        if (palette[key1] !== undefined && palette[key1] !== null && palette[key1].templateId !== undefined) {
          const materialPath = pathStringFromMaterialSrc(palette[key1]);
          const masonryFromSlot1 = MaterialToMasonry[materialPath];
          if (masonryFromSlot1 !== undefined) {
            // Check if the palette in the doc follows the convention.
            if (masonryFromSlot1.trimKind !== trimKind1) {
              warnings.push(
                "masonryTrim1 slot in a palette did not contain a " +
                  trimKind1 +
                  " material. Masonry material references may not migrate consistently.",
              );
            } else if (applyChanges) {
              palette[key1] = masonryFromSlot1.masonryId;
            }
            if (masonryFromSlot0 !== undefined && masonryFromSlot1.masonryId === masonryFromSlot0.masonryId) {
              if (applyChanges) {
                delete palette[key1];
              }
            } else {
              masonryConsistentAcrossSlots = false;
            }
          } else if (masonryFromSlot0 !== undefined) {
            masonryConsistentAcrossSlots = false;
          }
        }
      }
    };

    // for (const elevation of Object.values(json.elevations)) {
    //   // We'll check if the built-in palette referred to by the elevation has consistent masonry across slots.
    //   processPalette(exteriorPaletteLibrary[(elevation as any).baseSettings], false);
    // }

    for (const palette of Object.values(json.palettes)) {
      processPalette(palette, true);
    }

    // We replace all masonry trim references, even ones that specifically refer to Rowlock/Header,
    // since before this migration the trim kind did not have any effect and thus can be set wrong.
    const renameRegexMasonryTrim = /^(masonryTrim)([0-2]):(Base|Rowlock|Header)$/;

    const renameValue = (o: any, propertyName: string) => {
      if (!o.hasOwnProperty(propertyName)) {
        return;
      }

      const masonryTrimMatch = o[propertyName].match(renameRegexMasonryTrim);
      if (masonryTrimMatch) {
        // Migrate to just using the masonryTrim0 slot and the appropriate trim kind.
        let trimKind = trimKind0;
        if (masonryTrimMatch[2] === "1") {
          trimKind = trimKind1;
        } else if (masonryTrimMatch[2] === "2") {
          // The masonryTrim2 slot was not frequently used. On most palettes it would just fall back to the Header brick, so it's fine to migrate it to that.
          trimKind = trimKind1;
        }
        if (masonryConsistentAcrossSlots) {
          o[propertyName] = masonryTrimMatch[1] + "0:" + trimKind;
        } else {
          o[propertyName] = masonryTrimMatch[1] + masonryTrimMatch[2] + ":" + trimKind;
        }
      }
    };

    if (json.facades) {
      for (const facadeId in json.facades) {
        const facade = json.facades[facadeId];

        if (facade.spanStrata) {
          for (const spanStratum of facade.spanStrata) {
            renameValue(spanStratum, "name");
            renameValue(spanStratum, "material");
          }
        }

        if (facade.sweepStrata) {
          for (const spawnStratum of facade.sweepStrata) {
            renameValue(spawnStratum, "name");
            renameValue(spawnStratum, "material");
          }
        }
      }
    }

    if (json.settings) {
      for (const settingKey in json.settings) {
        const setting = json.settings[settingKey];

        renameValue(setting, "material");
        renameValue(setting, "trimMaterial");
        renameValue(setting, "apronCasingMaterial");
        renameValue(setting, "sideCasingMaterial");
        renameValue(setting, "headCasingMaterial");
        renameValue(setting, "exteriorWallMaterial");
        renameValue(setting, "roofMaterial");

        if (setting.fullReturnSettings) {
          renameValue(setting.fullReturnSettings, "rafterTailMaterial");
        }

        if (setting.columnTable) {
          for (const columnKey in setting.columnTable) {
            const column = setting.columnTable[columnKey];
            for (const section of column.sections) {
              renameValue(section, "paletteId");
            }
          }
        }
        if (setting.headCasingSlices) {
          for (const slice of setting.headCasingSlices) {
            renameValue(slice, "material");
          }
        }
      }
    }

    if (json.roomLayouts) {
      for (const layoutKey of Object.keys(json.roomLayouts)) {
        const layout = json.roomLayouts[layoutKey];
        if (layout.openingPatterns) {
          for (const openingPattern of layout.openingPatterns) {
            if (openingPattern.openings) {
              for (const opening of openingPattern.openings) {
                if (opening.exteriorCasing) {
                  renameValue(opening.exteriorCasing, "material");
                  renameValue(opening.exteriorCasing, "apronCasingMaterial");
                  renameValue(opening.exteriorCasing, "sideCasingMaterial");
                  renameValue(opening.exteriorCasing, "headCasingMaterial");
                  if (opening.exteriorCasing.headCasingSlices) {
                    for (const slice of opening.exteriorCasing.headCasingSlices) {
                      renameValue(slice, "material");
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    return json;
  },
};

export default migration_0_0_92;
